import Qs from 'query-string'
import deburr from 'lodash.deburr'

import { isMobile } from 'utils/deviceDetector'
import initialValueFilters from 'lib/filters'
import * as providers from './provider'
import {
  parseQueryString,
  parseSanitizeUrl,
  uniqueCheckboxesToggler,
  onChangeMin,
  onChangeMax,
} from './utils'

export default (setFilter, setError, initialState) => {
  const setField = (field, value) => {
    return setFilter(prev => ({ ...prev, [field]: value }))
  }

  const goToFirstPage = () => setField('page', 1)

  const setFieldManually = (field, value, setClickedBy = () => {}) => {
    setField(field, value)
    setField('presetProfile', false)
    goToFirstPage()
    setClickedBy('')
  }

  const setCampaign = (campaign, value) => {
    setFilter(prev => ({
      ...prev,
      campaigns: {
        ...prev.campaigns,
        [campaign]: value,
      },
    }))
  }

  const setCurrentBanner = (currentBanner, value, campaignName) => {
    const newCurrentBanner = [...currentBanner]
    if (value) {
      if (newCurrentBanner.includes(campaignName)) return
      newCurrentBanner.push(campaignName)
    } else {
      const index = newCurrentBanner.indexOf(campaignName)
      newCurrentBanner.splice(index, 1)
    }
    setField('currentBannerList', newCurrentBanner)
  }

  const updateQuery = () => {
    setFilter(prev => {
      const newQuery = parseQueryString(prev)
      if (newQuery === prev.query) {
        return prev
      }

      return {
        ...prev,
        query: newQuery,
      }
    })
  }

  const setFilterAndUpdateQuery = (field, value) => {
    setField(field, value)
    updateQuery()
  }

  const getLocalities = async () => {
    const [err, res = {}] = await providers.getLocalities()

    if (res.error || !res.data) {
      // eslint-disable-next-line
      console.error('localityFetchError', err)
      setField('localities', [])
      return setError(res.code || 500)
    }

    const localities = res.data.map(({ name, ...locality }) => ({
      ...locality,
      name,
      deburredName: deburr(name.toLowerCase()),
    }))

    setFilter(prev => {
      const newState = { ...prev, localities }
      const localitiesLoaded = localities.length
      const localityAlreadySet = !prev.locality && prev.locality !== ''

      if (localitiesLoaded && prev.slug && localityAlreadySet) {
        const locality = localities.find(place => place.slug === prev.slug)
        newState.locality = (locality && locality.name) || ''
      }

      return newState
    })

    return localities
  }

  const setLocalityField = locality => {
    setField('locality', locality)
    if (!locality) {
      setField('slug', '')
    }
  }

  const parseInitialFilters = ({ initialFilters, apiFilters }) => {
    const newFilters = { ...apiFilters }
    const apiFilterKeys = Object.keys(initialFilters || {})

    if (apiFilterKeys.length) {
      apiFilterKeys.forEach(apiFilterKey => {
        if (apiFilterKey === 'Desagio') {
          const value = initialFilters[apiFilterKey].toString()

          newFilters[apiFilterKey] = newFilters[apiFilterKey].map(
            apiFilter => ({
              ...apiFilter,
              checked:
                initialFilters[apiFilterKey] && value === apiFilter.value,
            })
          )
        }

        if (
          Array.isArray(newFilters[apiFilterKey]) &&
          apiFilterKey !== 'Desagio'
        ) {
          newFilters[apiFilterKey] = newFilters[apiFilterKey].map(apiFilter => {
            const initialFiltersArray =
              typeof initialFilters[apiFilterKey] === 'string'
                ? [initialFilters[apiFilterKey]]
                : [...initialFilters[apiFilterKey]]

            return {
              ...apiFilter,
              checked:
                initialFiltersArray &&
                initialFiltersArray.includes(apiFilter.value),
            }
          })
        }
      })
    }

    return newFilters
  }

  const fetchFilters = async () => {
    const filters = providers.getFilters()

    if (!filters || filters.error) {
      // eslint-disable-next-line
      console.error('filterFetchError', filters.error)
      return setError(filters.code || 500)
    }

    return setFilter(prev => {
      const newState = {
        ...prev,
        apiFilters: filters,
      }
      newState.filters = parseInitialFilters(newState)
      newState.query = parseQueryString(newState)
      delete newState.initialFilters

      return newState
    })
  }

  const setFilterByUrlParams = ({ slug = '', search = '' }) => {
    const parseSearch = search.replace(/--/g, ',')
    const parseSlug = slug.replace(/--/g, ',')
    const {
      valor,
      buscaID,
      visitas,
      'sem-acoes-judiciais': semAcoesJudiciais,
      'sem-dividas': semDividas,
      'campanha-exclusiva': campaigns,
      'busca-na-regiao': surroundingsSearch,
      ordenacao,
      pagina,
      hideBaseLayout,
      selectedVendorId,
      utm_campaign,
      utm_source,
      utm_medium,
      utm_content,
      ...apiFilters
    } = Qs.parse(parseSearch, { arrayFormat: 'comma' }) || {}

    setFilter(prev => {
      const newState = {
        ...prev,
        slug: parseSlug || prev.slug,
        searchId: parseSanitizeUrl('searchId', buscaID, prev),
        valueRange: parseSanitizeUrl('valueRange', valor, prev),
        visits: parseSanitizeUrl('visits', visitas, prev),
        noLegalActions: parseSanitizeUrl('visits', semAcoesJudiciais, prev),
        noDebts: parseSanitizeUrl('noDebts', semDividas, prev),
        campaigns: parseSanitizeUrl('campaigns', campaigns, prev),
        surroundingsSearch: parseSanitizeUrl(
          'surroundingsSearch',
          surroundingsSearch,
          prev
        ),
        initialFilters:
          parseSanitizeUrl('apiFilters', apiFilters, prev) ||
          prev.initialFilters,
        orderBy: parseSanitizeUrl('orderBy', ordenacao, prev),
        page: parseSanitizeUrl('page', pagina, prev),
        hideBaseLayout: hideBaseLayout || prev.hideBaseLayout,
        utm_campaign,
        utm_source,
        utm_medium,
        utm_content,
      }

      if (
        newState.valueRange &&
        newState.valueRange.max > initialState.valueRange.max
      ) {
        newState.valueRange.max = initialState.valueRange.max
      }

      if (prev.localities.length && newState.slug) {
        const locality = prev.localities.find(place => place.slug === slug)
        newState.locality = (locality && locality.name) || ''
      }
      const updatedFilter = {
        ...prev,
        ...newState,
      }

      if (prev.apiFilters) {
        updatedFilter.filters = parseInitialFilters(updatedFilter)
        delete updatedFilter.initialFilters
      }
      updatedFilter.query = parseQueryString(updatedFilter)
      return updatedFilter
    })
  }

  const clearFilters = () => {
    setFilter(prev => ({
      ...initialState,
      filters: prev.apiFilters,
      apiFilters: prev.apiFilters,
      localities: prev.localities,
      valueRange: { ...initialValueFilters.initialValueRange },
      locality: prev.locality,
      slug: prev.slug,
      cleared: true,
    }))
  }

  const scrollToId = () => {
    const element = document.getElementById('list')
    if (element) {
      element.scrollIntoView()
    }
  }

  const clearAllFilters = () => {
    setFilter(prev => ({
      ...initialState,
      filters: prev.apiFilters,
      apiFilters: prev.apiFilters,
      localities: prev.localities,
      valueRange: { ...initialValueFilters.initialValueRange },
      locality: undefined,
      slug: undefined,
      cleared: true,
    }))
  }

  const clearAllFiltersClick = () => {
    clearAllFilters()
    scrollToId()
    updateQuery()
  }

  const toggleFilter = (filterName, fieldName) => {
    setFilter(prev => {
      const newFilter = [...prev.filters[filterName]]

      const fieldIndex = newFilter.findIndex(field => field.value === fieldName)

      const field = { ...newFilter[fieldIndex] }

      newFilter.splice(fieldIndex, 1, { ...field, checked: !field.checked })

      if (!isMobile) {
        scrollToId()
      }

      return {
        ...prev,

        filters: {
          ...prev.filters,

          [filterName]: newFilter,
        },
      }
    })

    updateQuery()
  }

  const setFieldAndScroll = (name, value) => {
    setField(name, value)

    scrollToId()

    updateQuery()
  }

  const setLocalitiesFromUrl = (slug, localities) => {
    const localitiesSearch = []
    if (slug && localities) {
      const slugs = slug.split('--')
      slugs.forEach(element => {
        const locality = localities.filter(local => {
          return local.slug === element
        })
        localitiesSearch.push(locality[0])
      })
      setField('selectedLocalities', localitiesSearch)
    }
    updateQuery()
  }

  const setSelectedLocalities = value => {
    setField('selectedLocalities', value)
    goToFirstPage()
  }

  const removeSelectedLocality = (id, selectedLocalities) => {
    const removedLocality = [...selectedLocalities]
    removedLocality.splice(id, 1)

    setSelectedLocalities(removedLocality)
  }

  /**
   * Accesses $filter.filters on store and changes respective filter key (fieldName), toggling the corresponding checked box (fieldValue) and making sure the rest of the fields of this field stay falsy / unChecked. As, on this filter, just one checkbox can me checked at a single time
   * @param {string} fieldName
   * @param {string} fieldValue
   * @author Leandro Parisi
   */
  const toggleUniqueCheckbox = (fieldName, fieldValue) => {
    setFilter(prev => {
      const prevFilter = [...prev.filters[fieldName]]

      const newFilter = uniqueCheckboxesToggler(prevFilter, fieldValue)

      if (!isMobile) {
        scrollToId()
      }

      return {
        ...prev,
        filters: {
          ...prev.filters,
          [fieldName]: newFilter,
        },
      }
    })
    goToFirstPage()
  }

  const setFiltersModal = (field, fieldValue) => {
    setFilter(prev => ({
      ...prev,

      filtersModal: {
        ...prev.filtersModal,

        [field]: fieldValue,
      },
    }))
  }
  const setMaxValueRange = valueRange => newMaxValue => {
    return setField('valueRange', onChangeMax(newMaxValue, valueRange))
  }

  const setMinValueRange = valueRange => newMinValue => {
    return setField('valueRange', onChangeMin(newMinValue, valueRange))
  }

  return {
    setLocalityField,
    getLocalities,
    setField,
    toggleFilter,
    fetchFilters,
    goToFirstPage,
    setFilterByUrlParams,
    updateQuery,
    clearFilters,
    clearAllFilters,
    setFieldAndScroll,
    clearAllFiltersClick,
    setFilterAndUpdateQuery,
    setLocalitiesFromUrl,
    setSelectedLocalities,
    removeSelectedLocality,
    setFieldManually,
    toggleUniqueCheckbox,
    setFiltersModal,
    setMaxValueRange,
    setMinValueRange,
    setCampaign,
    setCurrentBanner,
  }
}
